import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyPhotoWebApiHandler_pollPhotoIds } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getMultiplexDetailsTags } from './multiplex/useMultiplexDetails';
import { getListingDetailsQueryKey } from './properties';

import type { Query } from '@zg-rentals/react-query5';
import type {
  GetUserPropertyDetailsResponseWebDto,
  ListingAlias,
  UserPropertyMultiplexGetBuildingSessionDetailsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

/** Mirrors the backend rentalManagerApi.photoUpload.uploadUrlValidityDuration setting. */
export const QR_PHOTO_UPLOAD_POLL_WINDOW_MS = 30 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;
/** Retry window for an unchanged-but-still-diverging photo id set, so a failed details refetch recovers. */
const INVALIDATE_COOLDOWN_MS = 30_000;

const toComparablePhotoIds = (photoIds: Array<string>) => [...photoIds].sort().join(',');

// The multiplex step's gallery reads the building session details, cached under these tags.
const isSessionDetailsQuery = (multiplexBuildingSessionId: string, listingAlias: string) => {
  const sessionTags = getMultiplexDetailsTags(multiplexBuildingSessionId, listingAlias);
  return ({ meta }: Pick<Query, 'meta'>) => sessionTags.every((tag) => meta?.tags?.includes(tag));
};

export const useQrPhotoUploadPolling = ({
  listingAlias,
  pollUntilMs,
  multiplexBuildingSessionId,
}: {
  listingAlias: string;
  pollUntilMs: number | undefined;
  multiplexBuildingSessionId?: string;
}) => {
  const queryClient = useQueryClient();
  const lastInvalidatedRef = useRef<{ photoIds: string; atMs: number } | null>(null);

  const { data, dataUpdatedAt } = useRentalManagerUserPropertyPhotoWebApiHandler_pollPhotoIds(
    { listingAlias: listingAlias as ListingAlias },
    {
      enabled: Boolean(listingAlias) && pollUntilMs !== undefined && Date.now() < pollUntilMs,
      refetchInterval: () => (pollUntilMs !== undefined && Date.now() < pollUntilMs ? POLL_INTERVAL_MS : false),
      refetchIntervalInBackground: false,
      refetchOnReconnect: false,
      meta: {
        tags: ['listingPhotoIds'],
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyPhotoWebApiHandler_pollPhotoIds',
        },
      },
    },
  );

  useEffect(() => {
    lastInvalidatedRef.current = null;
  }, [listingAlias, multiplexBuildingSessionId]);

  useEffect(() => {
    if (data == null) {
      return;
    }
    const detailsQueryKey = getListingDetailsQueryKey(listingAlias);
    const sessionDetailsPredicate = multiplexBuildingSessionId
      ? isSessionDetailsQuery(multiplexBuildingSessionId, listingAlias)
      : undefined;
    const cachedPhotos = sessionDetailsPredicate
      ? queryClient
          .getQueriesData<UserPropertyMultiplexGetBuildingSessionDetailsResponseWebDto>({
            predicate: sessionDetailsPredicate,
          })
          .find(([, sessionDetails]) => sessionDetails != null)?.[1]?.propertyPhotos
      : queryClient.getQueryData<GetUserPropertyDetailsResponseWebDto>(detailsQueryKey)?.propertyPhotos;
    if (cachedPhotos == null) {
      return;
    }
    const cachedPhotoIds = cachedPhotos.map((photo) => photo.id);
    const polledPhotoIds = toComparablePhotoIds(data.photoIds);
    if (toComparablePhotoIds(cachedPhotoIds) === polledPhotoIds) {
      return;
    }
    const lastInvalidated = lastInvalidatedRef.current;
    const isWithinCooldown =
      lastInvalidated?.photoIds === polledPhotoIds && Date.now() - lastInvalidated.atMs < INVALIDATE_COOLDOWN_MS;
    if (isWithinCooldown) {
      return;
    }
    lastInvalidatedRef.current = { photoIds: polledPhotoIds, atMs: Date.now() };
    queryClient.invalidateQueries(
      sessionDetailsPredicate ? { predicate: sessionDetailsPredicate } : { queryKey: detailsQueryKey },
    );
    // dataUpdatedAt: react-query structurally shares an unchanged photoIds array, so run this on every poll.
  }, [data, dataUpdatedAt, listingAlias, multiplexBuildingSessionId, queryClient]);
};

/** Polls for photos uploaded from a phone for as long as the generated link stays valid. */
export const useQrPhotoUploadPollSession = (listingAlias: string, multiplexBuildingSessionId?: string) => {
  const [pollSession, setPollSession] = useState<{ listingAlias: string; pollUntilMs: number } | undefined>();

  const startQrUploadPollWindow = useCallback(
    () => setPollSession({ listingAlias, pollUntilMs: Date.now() + QR_PHOTO_UPLOAD_POLL_WINDOW_MS }),
    [listingAlias],
  );

  // Scoped during render so a session started on a previously viewed listing never polls the current one.
  const pollUntilMs = pollSession?.listingAlias === listingAlias ? pollSession.pollUntilMs : undefined;

  useEffect(() => {
    if (pollUntilMs === undefined) {
      return;
    }
    const expiryTimeout = setTimeout(() => setPollSession(undefined), Math.max(0, pollUntilMs - Date.now()));
    return () => clearTimeout(expiryTimeout);
  }, [pollUntilMs]);

  useQrPhotoUploadPolling({ listingAlias, pollUntilMs, multiplexBuildingSessionId });

  return { startQrUploadPollWindow };
};
